<template>
  <div class="stall-package-page">
    <!-- 顶部 -->
    <header class="package-header">
      <div class="header-info">
        <h1>{{ packageName }}</h1>
        <p v-if="validDate" class="valid-date">📅 {{ $t('package.validDate') }}：{{ validDate }}</p>
      </div>
    </header>

    <!-- 商品信息 -->
    <div class="package-content">
      <div class="section-title">📦 {{ $t('navigation.products') }}{{ $t('package.productList') }}</div>
      
      <div v-if="products.length > 0" class="product-list">
        <div v-for="product in products" :key="product.id" class="product-item">
          <div class="product-info">
            <div class="product-name">{{ product.name }}</div>
            <div class="product-meta">
              <span class="market-price">{{ $t('package.marketPrice') }} ¥{{ product.marketPrice }}</span>
            </div>
          </div>
          <div class="product-action">
            <div class="price-tag">¥{{ product.wholesalePrice }}</div>
            <div class="quantity-control">
              <button @click="decreaseQuantity(product.id)" :disabled="getQuantity(product.id) === 0">-</button>
              <input 
                type="number" 
                :value="getQuantity(product.id)" 
                @input="updateQuantity(product.id, $event.target.value)"
                min="0"
              >
              <button @click="increaseQuantity(product.id)">+</button>
            </div>
          </div>
        </div>
      </div>
      
      <div v-else class="empty-state">
        <div class="empty-icon">📦</div>
        <p>{{ $t('product.noProduct') }}</p>
      </div>
    </div>

    <!-- 底部购物车 -->
    <div v-if="totalQuantity > 0" class="cart-bar">
      <div class="cart-info">
        <span class="cart-count">{{ totalQuantity }} {{ $t('package.items') }}</span>
        <span class="cart-total">{{ $t('package.total') }}：¥{{ totalPrice }}</span>
      </div>
      <button class="btn-submit" @click="submitOrder" :disabled="submitting">
        {{ submitting ? $t('package.submitting') : $t('package.submitOrder') }}
      </button>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'StallWholesalePackage',
  data() {
    return {
      packageId: null,
      packageName: '',
      validDate: '',
      products: [],
      quantities: {},
      submitting: false
    }
  },
  computed: {
    totalQuantity() {
      return Object.values(this.quantities).reduce((sum, qty) => sum + (qty || 0), 0)
    },
    totalPrice() {
      let total = 0
      this.products.forEach(product => {
        const qty = this.quantities[product.id] || 0
        total += product.wholesalePrice * qty
      })
      return total.toFixed(4)
    }
  },
  mounted() {
    this.packageId = this.$route.params.id
    this.loadPackageDetail()
  },
  methods: {
    async loadPackageDetail() {
      try {
        const res = await api.get(`/wholesaler/packages/${this.packageId}`)
        const pkg = res.data || res
        
        this.packageName = pkg.packageName
        if (pkg.validStartDate && pkg.validEndDate) {
          this.validDate = `${this.formatDate(pkg.validStartDate)} 至 ${this.formatDate(pkg.validEndDate)}`
        }
        
        this.products = pkg.products || []
        // 初始化数量
        this.products.forEach(p => {
          this.quantities[p.id] = 0
        })
      } catch (error) {
        console.error('Failed to load wholesale package:', error)
        this.$message.error(this.$t('package.loadFailed'))
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    getQuantity(productId) {
      return this.quantities[productId] || 0
    },
    updateQuantity(productId, value) {
      const qty = parseInt(value) || 0
      this.quantities[productId] = Math.max(0, qty)
    },
    increaseQuantity(productId) {
      this.quantities[productId] = (this.quantities[productId] || 0) + 1
    },
    decreaseQuantity(productId) {
      if (this.quantities[productId] > 0) {
        this.quantities[productId]--
      }
    },
    async submitOrder() {
      try {
        this.submitting = true
        
        // 构建订单商品
        const orderItems = this.products
          .filter(p => (this.quantities[p.id] || 0) > 0)
          .map(p => ({
            productId: p.id,
            productName: p.name,
            quantity: this.quantities[p.id],
            price: p.wholesalePrice,
            wholesalePackageId: this.packageId
          }))
        
        // 调用下单 API
        await api.post('/sale-orders', {
          items: orderItems,
          source: 'wholesale_package'
        })
        
        this.$message.success(this.$t('package.orderSuccess'))
        // 清空购物车
        this.products.forEach(p => {
          this.quantities[p.id] = 0
        })
      } catch (error) {
        console.error('Failed to submit order:', error)
        this.$message.error(error.message || this.$t('package.submitFailed'))
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.stall-package-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 80px;
}

.package-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px 16px;
}

.package-header h1 {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.valid-date {
  font-size: 13px;
  opacity: 0.9;
  margin: 0;
}

.package-content {
  padding: 16px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

.product-list {
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.product-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.product-item:last-child {
  border-bottom: none;
}

.product-info {
  flex: 1;
}

.product-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
}

.product-meta {
  font-size: 12px;
  color: #999;
}

.market-price {
  text-decoration: line-through;
}

.product-action {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.price-tag {
  font-size: 16px;
  font-weight: 700;
  color: #ff4d4f;
}

.quantity-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.quantity-control button {
  width: 28px;
  height: 28px;
  border: 1px solid #d9d9d9;
  background: white;
  border-radius: 4px;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quantity-control button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.quantity-control input {
  width: 50px;
  height: 28px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  text-align: center;
  font-size: 14px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.empty-state p {
  font-size: 14px;
  color: #999;
}

.cart-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #e8e8e8;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
}

.cart-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cart-count {
  font-size: 13px;
  color: #666;
}

.cart-total {
  font-size: 16px;
  font-weight: 700;
  color: #ff4d4f;
}

.btn-submit {
  padding: 12px 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
